import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Popover, PopoverModule } from 'primeng/popover';
import { Subscription } from 'rxjs';
import { TranslationService } from '../../services/translation.service';
import { AppNotification, NotificationsService } from '../../services/notifications.service';
import type { NotificationBellTranslations } from '../../i18n/notification-bell';
import en from '../../i18n/en/notification-bell';
import { formatRelativeTime } from '../../utils/relative-time';

type PanelState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Header bell (chamba-ai#411): shows the job offers the user matched with -
 * a shorter version of the notification email. Sits to the left of the
 * language selector. Clicking a notification marks it read and opens the
 * offer's own page in a new tab, exactly where the email's "Ver detalle" /
 * "Aplicar ahora" buttons go (no page inside the app).
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [PopoverModule],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.css'],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly notificationsService = inject(NotificationsService);
  private readonly translationService = inject(TranslationService);

  @ViewChild('popover') popover?: Popover;

  translations: NotificationBellTranslations = en;
  language = 'en';
  unreadCount = 0;
  notifications: AppNotification[] = [];
  state: PanelState = 'idle';
  open = false;

  private subscription = new Subscription();
  private destroyed = false;

  get t(): NotificationBellTranslations {
    return this.translations;
  }

  ngOnInit(): void {
    this.refreshTranslations();
    // TranslationService.onChange has no unsubscribe, so guard against
    // touching a destroyed component (the header is rebuilt on every login).
    this.translationService.onChange(() => {
      if (!this.destroyed) this.refreshTranslations();
    });

    this.subscription.add(this.notificationsService.unreadCount$.subscribe((n) => (this.unreadCount = n)));
    this.subscription.add(this.notificationsService.notifications$.subscribe((list) => (this.notifications = list)));
    this.notificationsService.startPolling();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscription.unsubscribe();
    this.notificationsService.stopPolling();
  }

  private refreshTranslations(): void {
    this.language = this.translationService.getCurrentLanguage() || 'en';
    this.translations = this.translationService.getNotificationBellTranslations() ?? en;
  }

  get badgeText(): string {
    return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
  }

  get bellLabel(): string {
    return this.unreadCount > 0
      ? this.t.BELL_LABEL_UNREAD.replace('{count}', String(this.unreadCount))
      : this.t.BELL_LABEL;
  }

  toggle(event: Event): void {
    this.popover?.toggle(event);
  }

  onShow(): void {
    this.open = true;
    this.refreshTranslations();
    this.load();
  }

  onHide(): void {
    this.open = false;
  }

  /** Reloads the list every time the panel opens. With nothing to show yet
   * the panel shows its loading / error state; with a list already on screen
   * a failed refresh is silent and the last known list stays. */
  load(): void {
    if (this.notifications.length === 0) this.state = 'loading';
    this.subscription.add(
      this.notificationsService.loadNotifications().subscribe({
        next: () => (this.state = 'ready'),
        error: () => (this.state = this.notifications.length === 0 ? 'error' : 'ready'),
      })
    );
  }

  onItemClick(notification: AppNotification): void {
    // Opened first, synchronously, inside the click: a popup opened after an
    // async call is what browsers block.
    const url = this.safeUrl(notification);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    if (!notification.read_at) {
      // Optimistic in the service; if the request fails the item simply goes
      // back to unread - the offer was already opened, nothing to tell the user.
      this.subscription.add(this.notificationsService.markAsRead(notification).subscribe({ error: () => undefined }));
    }
    this.popover?.hide();
  }

  markAllRead(): void {
    if (this.unreadCount === 0) return;
    this.subscription.add(this.notificationsService.markAllAsRead().subscribe({ error: () => undefined }));
  }

  /** The link comes from scraped data: only plain http(s) is ever opened
   * (never javascript:, data:, ...). */
  private safeUrl(notification: AppNotification): string | null {
    for (const candidate of [notification.payload?.url, notification.payload?.apply_url]) {
      if (!candidate) continue;
      try {
        const parsed = new URL(candidate);
        if (parsed.protocol === 'https:' || parsed.protocol === 'http:') return parsed.href;
      } catch {
        // not a valid absolute URL - try the next candidate
      }
    }
    return null;
  }

  titleOf(notification: AppNotification): string {
    return notification.payload?.title || this.t.JOB_FALLBACK_TITLE;
  }

  /** "Acme · Berlin, DE" - whichever parts exist. */
  metaOf(notification: AppNotification): string {
    return [notification.payload?.company, notification.payload?.location].filter(Boolean).join(' · ');
  }

  matchLabel(notification: AppNotification): string | null {
    const similarity = notification.payload?.similarity;
    if (similarity === null || similarity === undefined) return null;
    return this.t.MATCH_PERCENT.replace('{percent}', String(Math.round(similarity * 100)));
  }

  timeAgo(notification: AppNotification): string {
    return formatRelativeTime(notification.created_at, this.language);
  }
}
